/* eslint-disable indent */
import { clientbookApi } from '../core/clientbook-api';

/**
 * Maps a chat channel `type` to the REST base path of the service that owns it.
 *
 * Facebook / Instagram conversations are owned by the meta service
 * (`/meta/{platform}/chats`) and Google reviews by the google service
 * (`/google/reviews`). Native chats (`C`) are served by the messages service
 * under `/chats`. Mirrors the `draftEndpoint` mapping in services/userDraft.js.
 *
 * @param {'C'|'F'|'I'|'G'|undefined} type chat channel type
 * @returns {string} base path segment, e.g. `chats` or `meta/facebook/chats`
 */
export function chatChannelEndpoint(type) {
  switch (type) {
    case 'F':
      return 'meta/facebook/chats';
    case 'I':
      return 'meta/instagram/chats';
    case 'G':
      return 'google/reviews';
    default:
      return 'chats';
  }
}

/**
 * Translates a generic chat-status payload (`{ isRead, isOpen }`) into the shape
 * the owning service expects. Google reviews use `read` instead of `isRead`;
 * `isOpen` is common to every channel.
 *
 * @param {string} type chat channel type
 * @param {object} data generic status payload
 * @returns {object} payload in the owning service's expected shape
 */
export function chatChannelStatusPayload(type, data) {
  if (type === 'G' && data && 'isRead' in data) {
    const { isRead, ...rest } = data;
    return { ...rest, read: isRead };
  }
  return data;
}

/**
 * PATCH a chat channel's status (`isRead` / `isOpen`), routing to the service
 * that owns the channel and translating the payload accordingly.
 *
 * @param {{ chatId: string|number, type: string, data: object }} params
 * @returns {Promise} the underlying clientbookApi.patch promise
 */
export function updateChatChannel({ chatId, type, data }) {
  return clientbookApi.patch({
    url: `/${chatChannelEndpoint(type)}/${chatId}`,
    data: chatChannelStatusPayload(type, data),
  });
}
